import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from "lucide-react";

const ToastContext = createContext(null);

let _id = 0;

const ICONS = {
  success: CheckCircle2,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
};

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const add = useCallback((message, type = "info") => {
    const id = ++_id;
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 3500);
  }, []);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useMemo(
    () => ({
      success: (msg) => add(msg, "success"),
      error: (msg) => add(msg, "error"),
      info: (msg) => add(msg, "info"),
      warning: (msg) => add(msg, "warning"),
    }),
    [add]
  );

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <div className="fixed bottom-5 right-5 z-50 flex w-80 flex-col gap-2">
        {toasts.map((t) => {
          const Icon = ICONS[t.type] || Info;
          return (
            <div
              key={t.id}
              onClick={() => dismiss(t.id)}
              className={`flex cursor-pointer items-start gap-3 rounded-2xl border px-4 py-3 text-sm font-medium shadow-lg transition-all
                ${t.type === "success" ? "border-emerald-200 bg-emerald-50 text-emerald-800" : ""}
                ${t.type === "error" ? "border-rose-200 bg-rose-50 text-rose-800" : ""}
                ${t.type === "warning" ? "border-amber-200 bg-amber-50 text-amber-800" : ""}
                ${t.type === "info" ? "border-sky-200 bg-sky-50 text-sky-800" : ""}
              `}
            >
              <Icon className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <p className="flex-1 leading-snug">{t.message}</p>
              <X className="mt-0.5 h-4 w-4 flex-shrink-0 opacity-40" />
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside ToastProvider");
  return ctx;
}
